import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { FilterDatePicker } from '../ui/date-picker';
import { Plus, ToggleLeft, ToggleRight, Trash2, Copy, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 0,
    max_uses: '',
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/coupons`);
      setCoupons(response.data || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    // Set default dates
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      max_uses: '',
      valid_from: today.toISOString().split('T')[0],
      valid_until: nextMonth.toISOString().split('T')[0]
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.discount_value || !formData.valid_from || !formData.valid_until) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const data = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseInt(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(formData.valid_until).toISOString()
      };

      await axios.post(`${API}/admin/coupons`, data);
      toast.success('Coupon created successfully');
      setShowDialog(false);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleToggle = async (couponId) => {
    try {
      const response = await axios.put(`${API}/admin/coupons/${couponId}`);
      toast.success(`Coupon ${response.data.is_active ? 'activated' : 'deactivated'}`);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to toggle coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Discount Coupons</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Create and manage discount codes for users
              </p>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No coupons created yet</p>
              <Button onClick={handleCreate} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Coupon
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Valid Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.map((coupon) => (
                    <TableRow key={coupon.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-lg">{coupon.code}</span>
                          <button
                            onClick={() => copyCode(coupon.code)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        {coupon.discount_type === 'percentage' ? (
                          <Badge className="bg-green-600 text-white">
                            {coupon.discount_value}% OFF
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-600 text-white">
                            ₹{coupon.discount_value} OFF
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-semibold">{coupon.used_count}</span>
                          {coupon.max_uses ? ` / ${coupon.max_uses}` : ' / ∞'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          <div>{new Date(coupon.valid_from).toLocaleDateString()}</div>
                          <div className="text-xs">to {new Date(coupon.valid_until).toLocaleDateString()}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={coupon.is_active ? "success" : "secondary"}>
                          {coupon.is_active ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(coupon.id)}
                          >
                            {coupon.is_active ? (
                              <ToggleRight className="w-4 h-4" />
                            ) : (
                              <ToggleLeft className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(coupon.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create New Coupon</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Coupon Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="WELCOME50"
                className="font-mono uppercase"
              />
              <p className="text-xs text-gray-500 mt-1">Letters and numbers only (will be converted to uppercase)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type *</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({...formData, discount_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                  placeholder="10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.discount_type === 'percentage' ? '10 = 10% off' : '100 = ₹100 off'}
                </p>
              </div>
            </div>

            <div>
              <Label>Max Uses (Optional)</Label>
              <Input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                placeholder="Leave empty for unlimited"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid From *</Label>
                <FilterDatePicker
                  date={formData.valid_from ? new Date(formData.valid_from) : null}
                  onDateChange={(date) => setFormData({...formData, valid_from: date ? format(date, 'yyyy-MM-dd') : ''})}
                  placeholder="Select start date"
                />
              </div>

              <div>
                <Label>Valid Until *</Label>
                <FilterDatePicker
                  date={formData.valid_until ? new Date(formData.valid_until) : null}
                  onDateChange={(date) => setFormData({...formData, valid_until: date ? format(date, 'yyyy-MM-dd') : ''})}
                  placeholder="Select end date"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1"
            >
              Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CouponManagement;
